import React, { FC, useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { ReactTyped } from 'react-typed';
import Head from './head/Head';
import Header from './header/Header';
import Footer from './footer/Foooter';
import AppRoot from './global/AppRoot';
import AppWrap from './global/AppWrap';

import FileManagerProvider from '../pages/app/file-manager/components/Context';

interface PaymentAlertDetails {
  overdueCount: number;
  totalDueAmount: number;
  oldestDueDate?: string;
  autoHoldEnabled?: boolean;
  autoHoldDate?: string;
}

interface LayoutProps {
  title?: string;
  [key: string]: any;
}

const fmtDate = (dt: string) =>
  new Date(dt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

const fmtAmt = (v: number) =>
  Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const Layout: FC<LayoutProps> = ({ title, ...props }) => {
  const [paymentAlert, setPaymentAlert] = useState<PaymentAlertDetails | null>(null);
  const [isAlertOpen, setIsAlertOpen] = useState(false);

  useEffect(() => {
    const alertData = sessionStorage.getItem('pending_payment_alert');
    if (alertData) {
      sessionStorage.removeItem('pending_payment_alert');
      try {
        const parsed = JSON.parse(alertData);
        const d: PaymentAlertDetails = parsed.details || {};
        setPaymentAlert(d);
        setIsAlertOpen(true);
      } catch {}
    }
  }, []);

  const buildSingleString = (d: PaymentAlertDetails): string => {
    const parts: string[] = [
      `<strong>Pending Invoices:</strong> ${d.overdueCount || 0}`,
      `<strong>Total Due:</strong> <span style="color:#c0392b;font-weight:700">₹ ${fmtAmt(d.totalDueAmount)}</span>`,
    ];
    if (d.oldestDueDate) {
      parts.push(`<strong>Overdue Since:</strong> ${fmtDate(d.oldestDueDate)}`);
    }
    if (d.autoHoldEnabled && d.autoHoldDate && new Date(d.autoHoldDate) > new Date()) {
      parts.push(`<strong style="color:#c0392b">Account Hold:</strong> <span style="color:#c0392b">${fmtDate(d.autoHoldDate)}</span>`);
    }
    parts.push(`<span style="color:#856404">Please contact your administrator.</span>`);
    return parts.join('&nbsp;&nbsp;|&nbsp;&nbsp;');
  };

  const handleDismiss = () => setIsAlertOpen(false);

  return (
    <FileManagerProvider>
      <Head title={title || 'Loading'} />

      {paymentAlert && isAlertOpen && (
        <>
          {/* Blur/dim overlay — click anywhere to dismiss */}
          <div
            onClick={handleDismiss}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 1058,
              backdropFilter: 'blur(4px)',
              WebkitBackdropFilter: 'blur(4px)',
              backgroundColor: 'rgba(0, 0, 0, 0.35)',
              cursor: 'pointer',
            }}
          />

          {/* Payment-due banner — pinned above everything including the sticky header */}
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              zIndex: 1059,
              background: 'linear-gradient(90deg, #fff3cd 0%, #ffe08a 50%, #fff3cd 100%)',
              borderBottom: '2px solid #f0ad4e',
              boxShadow: '0 3px 14px rgba(0,0,0,0.22)',
              padding: '10px 48px 10px 16px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              minHeight: '42px',
            }}
          >
            {/* Warning icon badge */}
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#e67e22',
                color: '#fff',
                borderRadius: '6px',
                padding: '3px 10px',
                fontWeight: 700,
                fontSize: '12px',
                letterSpacing: '0.5px',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              ⚠ PAYMENT DUE
            </span>

            {/* Typed message */}
            <span style={{ fontSize: '13px', color: '#533f03', flex: 1 }}>
              <ReactTyped
                strings={[buildSingleString(paymentAlert)]}
                typeSpeed={18}
                showCursor={false}
                loop={false}
              />
            </span>

            {/* Close button */}
            <button
              onClick={handleDismiss}
              aria-label="Close"
              style={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '18px',
                color: '#856404',
                lineHeight: 1,
                padding: '2px 6px',
                borderRadius: '4px',
              }}
            >
              ×
            </button>
          </div>
        </>
      )}

      <AppRoot>
        <AppWrap>
          <Header fixed />
          <Outlet />
          <Footer />
        </AppWrap>
      </AppRoot>
    </FileManagerProvider>
  );
};

export default Layout;
